import { createContext, useContext, useState, useCallback, lazy, Suspense, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import lazyWithReload from '../../utils/lazyWithReload';
import DrawerLoadingFallback from '../common/DrawerLoadingFallback';
const ApprovalDrawer = lazyWithReload(() => import('./ApprovalDrawer'), 'ApprovalDrawer');
import { sid } from '../../utils/shortId';
import { registerEntityOpener } from '../../utils/openEntity';
import { noProviderOpener } from '../../utils/drawerNoProvider';

/**
 * ApprovalDrawerContext — URL-driven global approval drawer (mirror TaskDrawerContext).
 * Source of truth: query param `?approvalId=<short>`. Khi đổi → drawer mở/đóng.
 *
 * API: useApprovalDrawer() → { openApproval(id), closeApproval(), version, bumpVersion, openApprovalId }
 *
 * Mounted ở main.jsx (top-level) để mọi page (chat, tasks, ...) đều có thể mở drawer.
 */
const Ctx = createContext(null);

// Provider không nằm trên cây React của người gọi (hay gặp nhất: nội dung xem TRONG một
// drawer KHÁC) → lui về sổ đăng ký module-scope. Xem utils/drawerNoProvider.
const NO_PROVIDER = {
  openApproval: noProviderOpener('approval'),
  closeApproval: () => {},
  version: 0,
  bumpVersion: () => {},
  openApprovalId: null,
};

export function ApprovalDrawerProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const approvalId = searchParams.get('approvalId');
  const [version, setVersion] = useState(0);

  const openApproval = useCallback((id) => {
    if (!id) return;
    const short = sid(id);
    setSearchParams(prev => {
      if (prev.get('approvalId') === short) return prev;
      const next = new URLSearchParams(prev);
      next.set('approvalId', short);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const closeApproval = useCallback(() => {
    setSearchParams(prev => {
      if (!prev.get('approvalId')) return prev;
      const next = new URLSearchParams(prev);
      next.delete('approvalId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const bumpVersion = useCallback(() => setVersion(v => v + 1), []);

  // Khai báo với kernel utils/openEntity: "loại này mở bằng POPUP" → link/card trong chat
  // trỏ tới bản ghi loại này bung popup tại chỗ, không nhảy trang chi tiết (trắng trang chờ chunk).
  useEffect(() => registerEntityOpener('approval', openApproval), [openApproval]);

  return (
    <Ctx.Provider value={{ openApproval, closeApproval, version, bumpVersion, openApprovalId: approvalId }}>
      {children}
      {approvalId && (
        <Suspense fallback={<DrawerLoadingFallback onClose={closeApproval} />}>
          <ApprovalDrawer
        open={!!approvalId}
        approvalId={approvalId}
        onClose={closeApproval}
        onChange={bumpVersion}
        onActionDone={bumpVersion}
      />
        </Suspense>
      )}
    </Ctx.Provider>
  );
}

export function useApprovalDrawer() {
  return useContext(Ctx) || NO_PROVIDER;
}
